import { useEffect } from 'react';

/**
 * Mounts a single global rAF-throttled scroll listener that writes
 * `--scroll-y` (px) and `--scroll-progress` (0–1) to the document root,
 * so CSS consumers can drive parallax / scroll-coupled effects via
 * `transform: translate3d(0, calc(var(--scroll-y, 0px) * X), 0)` without
 * triggering any React renders on scroll.
 *
 * Module-level ref counter survives React 18 StrictMode's double-mount and
 * multiple consumers across the app (only one listener is ever attached).
 *
 * `prefers-reduced-motion: reduce` honoured by skipping listener attachment
 * entirely — the CSS var fallback `var(--scroll-y, 0px)` resolves to 0px and
 * consumers degrade to a static layout. Pattern per workspace lesson L-190.
 */

let refCount = 0;
let raf = 0;
let scrollListener: (() => void) | null = null;
let resizeListener: (() => void) | null = null;
let mqListener: ((e: MediaQueryListEvent) => void) | null = null;
let mq: MediaQueryList | null = null;

// The scroll extent (content height − viewport) is near-constant; reading it
// every scroll frame forced a synchronous reflow per frame (a layout read after
// the previous frame's CSS-var writes). Cache it, and re-measure only when it
// actually changes: on resize, and via a ResizeObserver for content-height
// changes (images / lazy content loading). Per-frame path now reads only scrollY.
let scrollMax = 1;
let ro: ResizeObserver | null = null;

function measure() {
  scrollMax = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
}

function compute() {
  raf = 0;
  const root = document.documentElement;
  const y = window.scrollY;
  root.style.setProperty('--scroll-y', `${y}px`);
  root.style.setProperty('--scroll-progress', `${Math.min(1, Math.max(0, y / scrollMax))}`);
}

function attach() {
  if (scrollListener) return;
  const onScroll = () => {
    if (raf) return;
    raf = requestAnimationFrame(compute);
  };
  const onResize = () => {
    measure();
    if (raf) return;
    raf = requestAnimationFrame(compute);
  };
  scrollListener = onScroll;
  resizeListener = onResize;
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onResize);
  ro = new ResizeObserver(measure);
  ro.observe(document.body);
  measure();
  compute();
}

function detach() {
  if (raf) {
    cancelAnimationFrame(raf);
    raf = 0;
  }
  if (scrollListener) {
    window.removeEventListener('scroll', scrollListener);
    scrollListener = null;
  }
  if (resizeListener) {
    window.removeEventListener('resize', resizeListener);
    resizeListener = null;
  }
  if (ro) {
    ro.disconnect();
    ro = null;
  }
  const root = document.documentElement;
  root.style.removeProperty('--scroll-y');
  root.style.removeProperty('--scroll-progress');
}

export function useScrollVars(): void {
  useEffect(() => {
    refCount += 1;

    if (refCount === 1) {
      mq = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (!mq.matches) attach();
      mqListener = (e) => {
        if (e.matches) detach();
        else attach();
      };
      mq.addEventListener('change', mqListener);
    }

    return () => {
      refCount -= 1;
      if (refCount === 0) {
        if (mq && mqListener) mq.removeEventListener('change', mqListener);
        mq = null;
        mqListener = null;
        detach();
      }
    };
  }, []);
}
